import PropTypes from 'prop-types'

import styled, { css } from 'styled-components'

import colors from 'components/utils/colors'

import { NavBarMenuItemStyles } from './NavBarMenuItem'

import { useState, useRef, cloneElement } from 'react'

const RightCaret = styled.span`
  margin: 5px 0 2px 4px;
`

const StyledNavBarSubMenu = styled.div`
  ${NavBarMenuItemStyles}

  &:focus {
    outline: none;
  }

  ${RightCaret} {
    &:hover:before {
      color: ${colors.secondary40};
    }
  }

  ${props => props.selected && css`
    color: ${colors.secondary20};
    background-color: ${colors.primary80};
  `}

  ${props => props.opened && css`
    background-color: ${colors.primary90};
  `}
`

const StyledNavBarSubMenuContent = styled.div`
  position: absolute;
  min-width: 124px;
  z-index: 1;
  left: 100%;
  margin-top: -24px!important;
  transition: all 0.25s linear 0s;

  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.29);
  background-color: #FFFFFF;

  margin-top: 1px;

  display: none;

  ${props => props.opened && css`
    display: block;
  `}
`

const NavBarSubMenu = ({ selected, title, children, innerRef }) => {
  const [opened, setOpened] = useState(false)
  const [focused, setFocused] = useState(false)

  // We use this to select first menu item during keyboard arrow navigation
  // See https://stackoverflow.com/a/67955503
  const firstChildRef = useRef(null)

  const onKeyDown = (e) => {
    if (opened) {
      if (e.key === 'ArrowUp') {
        e.target.previousElementSibling?.focus()
      } else if (e.key === 'ArrowDown') {
        e.target.nextElementSibling?.focus()
      }

      if (
        e.key === 'ArrowLeft' ||
        e.key === 'Escape' ||
        (e.shiftKey && e.key === 'Tab' && e.target === firstChildRef.current)
      ) {
        setOpened(false)
        setTimeout(() => { innerRef.current.focus() }, 0)
        // We need both so sub menu will be closed on first Esc hit and main menu on second.
        // With just stopPropagation, main menu won't be closed on second Esc hit.
        // See https://stackoverflow.com/a/44587299
        e.stopPropagation()
        e.nativeEvent.stopImmediatePropagation()
      }
    }

    // We have separate if here so those keys will work even when menu is already opened
    // This is useful when menu was opened with mouse and user starts navigating with KB after that
    if (e.key === 'Enter' || e.key === 'ArrowRight') {
      setOpened(true)
      setTimeout(() => { firstChildRef.current.focus() }, 0)
      e.stopPropagation()
    }
  }

  const onBlur = (e) => {
    const currentTarget = e.currentTarget
    // Check the newly focused element in the next tick of the event loop
    setTimeout(() => {
      // Check if the new activeElement is a child of the original container
      const el = document.activeElement
      // Only handle keyboard-driven focus moves here (focus lands on a real element).
      // Pointer clicks are handled by onMouseLeave. This avoids hiding the submenu
      // content mid-click, which Safari treats as a suppressed click and breaks links.
      if (el && el !== document.body && !currentTarget.contains(el)) {
        closeAndUnfocus()
      }
    }, 0)
  }

  const closeAndUnfocus = () => {
    setOpened(false)
    setFocused(false)
  }

  const onMouseEnter = (e) => {
    setOpened(true)
    e.currentTarget.focus()
  }

  return (
    <StyledNavBarSubMenu
      onMouseEnter={onMouseEnter}
      onMouseLeave={closeAndUnfocus}
      onFocus={() => setFocused(true)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      selected={selected}
      opened={opened}
      focused={focused}
      ref={innerRef}
      tabIndex={0}
      data-test='navbar_submenu'
    >
      {title}
      <RightCaret className='icon-arrow-right-small-8' />
      <StyledNavBarSubMenuContent
        opened={opened}
        onClick={closeAndUnfocus}
        data-test='navbar_submenu_content'
      >
        {children.map((child, index) => {
          if (index === 0) {
            return cloneElement(child, { innerRef: firstChildRef, key: index })
          }
          return cloneElement(child, { key: index })
        })}
      </StyledNavBarSubMenuContent>
    </StyledNavBarSubMenu>
  )
}

NavBarSubMenu.propTypes = {
  selected: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
}

export default NavBarSubMenu
